import React from 'react';

import Button, { LoadingButton } from '@atlaskit/button';

import { EllipseStyled } from '@qality/common-components/src/atoms/Ellipse';
import { ExecutionStatusLozenge } from '@qality/common-components/src/atoms/ExecutionStatusLozenge';
import { LastExecution, TestCase } from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueDialogs,
  StorageOfUniqueServices,
} from '@qality/common-service';
import FocusUtils from '@qality/common-utils/src/FocusUtils';

import {
  LastExecutionBarStyled,
  LastExecutionButtonsStyled,
  LastExecutionStatusStyled,
  LastExecutionTextStyled,
  LastExecutionTextSubtleStyled,
} from './LastExecutionBar.styled';

type Props = {
  testCase: TestCase;
  issueKey: string;
  lastExecution: LastExecution;
};

export function LastExecutionBar({
  testCase,
  issueKey,
  lastExecution,
}: Props): JSX.Element {
  const openTestCaseExecutionDialog = async () => {
    const testCaseId = (await testCase.issue).id;
    StorageOfUniqueDialogs.get(
      'TestCaseExecutionDialog'
    ).openTestCaseExecutionDialog(testCaseId, testCase.name);
    FocusUtils.unfocusActiveElement();
  };

  const openTestCaseExecutionHistoryPage = async () => {
    const testCaseKey = (await testCase.issue).key;
    const url = await StorageOfUniqueServices.get(
      'URLService'
    ).getTestCaseExecutionHistoryUrl(testCaseKey, issueKey);

    window.open(url, '_parent');
  };

  const getExecutionData = (): {
    lastExecutionText: string;
    InnerComponent: React.ReactNode;
  } => {
    if (lastExecution) {
      return {
        lastExecutionText: DateFormatService.formatDate(
          lastExecution.createdDate,
          'longDateFormat'
        ),
        InnerComponent: (
          <ExecutionStatusLozenge status={lastExecution.status} />
        ),
      };
    }

    return {
      lastExecutionText: 'No previous executions',
      InnerComponent: null,
    };
  };

  const { lastExecutionText, InnerComponent } = getExecutionData();

  return (
    <LastExecutionBarStyled>
      <LastExecutionStatusStyled>
        <LastExecutionTextSubtleStyled>
          LAST EXECUTION
        </LastExecutionTextSubtleStyled>
        <EllipseStyled subtler />
        <LastExecutionTextStyled>{lastExecutionText}</LastExecutionTextStyled>
        {InnerComponent}
      </LastExecutionStatusStyled>
      <LastExecutionButtonsStyled>
        <Button
          appearance='subtle-link'
          onClick={openTestCaseExecutionHistoryPage}
        >
          Show execution history
        </Button>
        <LoadingButton
          onClick={openTestCaseExecutionDialog}
          appearance='subtle-link'
        >
          Execute
        </LoadingButton>
      </LastExecutionButtonsStyled>
    </LastExecutionBarStyled>
  );
}
